/** Flat scheduled-graph wire types for the unified dependency editor.
 *
 *  ONE shape served by `POST /api/v2/schedules/<sid>/dep-editor/graph` and
 *  consumed by both the pull-plan and Takt-board dep editors. It represents the
 *  P6 SCHEDULE (activities + predecessor edges), NOT the Takt plan — the Takt
 *  fields (section / zone / work code / color) are display-only metadata hung
 *  off each node and never change the graph's scope or shape.
 *
 *  Every activity is ONE uniform node type. Untagged, tagged, and new
 *  (duplicated) activities are indistinguishable; the only axes are:
 *    is_new    — born in this draft (pending_new_activities), not yet committed
 *    in_scope  — a section member (or a draft new activity). An out-of-scope
 *                endpoint of an edge is an external CONTEXT node: read-only,
 *                shown so the user sees what gates / is gated by the section,
 *                but its committed dates are never recomputed.
 *
 *  Mirrors the backend builder in services/pull_plan/dep_graph.py.
 */

import type { PredType } from "../utils/p6";

/** Short relationship form the graph speaks — converted from the DB's PR_*. */
export type DepGraphPredType = "FS" | "SS" | "FF" | "SF";

/** Takt zone tuple (display-only metadata). */
export interface DepGraphZone {
  a: string;
  b: string;
  c: string;
}

/** One uniform activity node. `id` is a real activity id (as a string) for a
 *  committed activity, or a draft_id for a pending new activity. */
export interface DepGraphActivity {
  id: string;
  /** Born in this draft (pending_new_activities); not yet committed. */
  is_new: boolean;
  /** Section member / draft new activity (editable). false = external context
   *  node — read-only, committed dates, shown only as an edge endpoint. */
  in_scope: boolean;
  /** True only on `DepGraph.link_candidates` entries — a completed section
   *  member surfaced ONLY as a dependency-search target (never a rendered row).
   *  Absent/false on the rendered `activities`. */
  link_candidate?: boolean;
  name: string;
  /** P6 activity code (e.g. "A00125") — the UNIQUE per-activity identifier.
   *  Work code + name can repeat across activities; this disambiguates them. */
  task_code: string;
  /** Raw P6 status, e.g. "TK_NotStart" / "TK_Active" / "TK_Complete". */
  status: string;
  /** Raw P6 task_type, e.g. "TT_Task" / "TT_Mile" / "TT_FinMile". */
  task_type: string;
  duration_days: number;
  /** The COLUMN dates P6 shows (ISO "YYYY-MM-DD" or null): a started/completed
   *  activity's Start is its ACTUAL start; an in-progress Finish is the remaining
   *  early finish; a completed Finish is the actual finish; not-started uses its
   *  early dates. */
  start: string | null;
  finish: string | null;
  /** The progressed portion — drawn solid up to the data date. Null until started. */
  actual_start: string | null;
  actual_finish: string | null;
  /** The REMAINING-early (CPM) dates the schedule drives off: where the
   *  remaining-work bar sits and where dependency ARROWS attach (never the actual
   *  start — an out-of-sequence start would make FS arrows point backwards). A
   *  completed driver's drive_start == drive_finish == its retained CPM point. */
  drive_start: string | null;
  drive_finish: string | null;
  complete: boolean;
  /** "" when untagged. */
  work_code: string;
  /** The work code's display name (Takt_Prefix) — what the board labels the bar
   *  with. "" when none. Tagged in-scope bars show this over `name`. */
  work_name: string;
  /** Takt_SectionName (display-only). */
  section: string;
  /** Takt_A/B/C (display-only). */
  zone: DepGraphZone;
  /** Hex fill from Takt_Color via work code; "" when none. */
  color: string;
  /** P6 date constraint (e.g. "CS_MSOA") + its date, or null. Badged in the dep
   *  editor, flagged driving when drive_start/drive_finish lands on cstr_date. */
  cstr_type: string | null;
  cstr_date: string | null;
}

/** One predecessor edge of a successor. Edges are keyed by SUCCESSOR id in the
 *  graph's `edges` map; this is one entry in that successor's list. */
export interface DepGraphEdge {
  /** id-or-draft_id of the predecessor; resolves to an `activities` key. */
  pred_id: string;
  type: DepGraphPredType;
  /** Lag in HOURS (the storage unit) — never truncated to whole days on the
   *  wire. The UI converts to workdays (/8) for display only. */
  lag_hours: number;
  /** Committed Predecessor.id — kept for an UPDATED committed edge too, so it
   *  can be re-edited any number of times. null only for a draft-added edge. */
  edge_id: number | null;
  /** Born in this draft (a pending_dep_edits add) — NOT an updated committed
   *  edge, which keeps its edge_id and is_new=false. */
  is_new: boolean;
  /** DERIVED server-side: the two endpoints' in_scope differ. */
  crosses: boolean;
  /** DERIVED server-side by the forward pass: this predecessor is the one whose
   *  date actually set the successor's early start (the binding / driving
   *  dependency; ties → multiple). False for slack preds and for ties into a
   *  started/completed successor. Recomputed on every graph build, so it tracks
   *  edits live. */
  driving: boolean;
}

/** An outside tie the draft severs — by deleting its in-section endpoint or
 *  by removing the tie itself. The committed edge is gone from the sandbox
 *  graph, so the tie's facts ride here instead — the Anchors review (and the
 *  publish confirm) keep showing it until the outside work is re-homed or the
 *  sever is undone. The external endpoint IS still emitted in `activities`
 *  (resolve via external_id). */
export interface SeveredAnchor {
  pred_id: string;
  succ_id: string;
  type: DepGraphPredType;
  lag_hours: number;
  /** The in-section endpoint: the activity this draft deletes, or (untie) the
   *  kept activity the tie is cut from. */
  deleted_id: string;
  deleted_name: string;
  deleted_task_code: string;
  /** The surviving outside endpoint — a key into `activities`. */
  external_id: string;
  /** From the section's point of view: "in" = the external fed the section
   *  activity; "out" = the section activity fed the external. */
  direction: "in" | "out";
  /** What severed it: an endpoint delete, or the tie itself removed. */
  reason?: "delete" | "untie";
  /** True when this tie was the BINDING (driving) edge against the deleted work
   *  in the pre-repull schedule — the external drove the deleted activity ("in")
   *  or the deleted activity drove the external ("out"). Flag it so the planner
   *  re-homes the load-bearing ties first. */
  was_driving?: boolean;
}

/** The full flat graph. `edges` is keyed by SUCCESSOR id -> its predecessor
 *  edges. Successor adjacency (a node's outgoing edges) is derived by
 *  inverting this map on the client. */
export interface DepGraph {
  activities: Record<string, DepGraphActivity>;
  /** Completed section members offered as dependency-search candidates only —
   *  NOT rendered as rows/drivers (kept out of `activities` so the row/driver/
   *  count logic is untouched). Empty/absent when the section has no completes. */
  link_candidates?: DepGraphActivity[];
  edges: Record<string, DepGraphEdge[]>;
  /** Project data date (ISO "YYYY-MM-DD") or null. Position of the strip's
   *  data-date ("now") line. */
  data_date: string | null;
  /** Outside ties severed by the draft's staged deletes (pull-plan only). */
  severed_anchors?: SeveredAnchor[];
  /** Set when staged ties form a LOOP: the pass couldn't run, every node is
   *  shown at its stored dates until the loop is removed. */
  cycle_error?: string | null;
  /** The activities around the loop, in order (P6 "loop detected"). */
  cycle_activities?: Array<{ id: string; task_code: string; name: string }>;
}

/** Map the short graph relationship form to the DB / FE PR_* form used by the
 *  shared chip controls and the draft payload's edge edits. */
export function predTypeFromShort(t: DepGraphPredType): PredType {
  return `PR_${t}` as PredType;
}

/** Map a PR_* form back to the short form the graph speaks. */
export function shortFromPredType(t: PredType): DepGraphPredType {
  return t.replace(/^PR_/, "") as DepGraphPredType;
}
